import * as React from 'react';
import {
	Box,
	Table,
	Header,
	TextFilter,
	Pagination,
	CollectionPreferences,
	SpaceBetween,
	Tabs,
	Container,
	ColumnLayout,
	Link,
	Flashbar,
} from '@amzn/awsui-components-console';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { TaskTypeListItem } from 'src/stores/taskTypeListStore/taskTypeListStore.interface';
import {
	SDS_APIGATEWAY_ENDPOINT,
	SNOW_NXT_WORKFLOW_ENDPOINT,
} from 'src/config';
import { convertOperationsIntoTaskSchema } from 'src/stores/taskTypeListStore/taskTypeListStoreUtils';
import { ListOperationsByStatusResponse } from '@amzn/snow-data-service-typescript-client';
import { DOMAIN_OPTIONS } from 'src/components/createWorkflow/CreateWorkflowConstants';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import {
	COLUMN_DEFINITIONS,
	DEFAULT_COLUMN_DISPLAY,
	HELP_PANEL_CONFIG,
	PAGE_SIZE_PREFERENCE,
	STRIPED_ROWS_PREFERENCE,
	WRAP_LINES_PREFERENCE,
} from './SupportedTaskTypesConstants';
import { TableWithNoData } from 'src/components/commonComponents/table/tableWithNoData';
import { getJwtToken } from 'src/authentication/cognito';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';

interface SupportedTaskTypesProps {
	openTools?: () => void;
	setTools?: (config: {
		title: React.ReactNode;
		content: React.ReactNode;
		links: Array<{ href: string; text: string }>;
	}) => void;
	hideTools?: () => void;
}

const SupportedTaskTypes: React.FC<SupportedTaskTypesProps> = ({
	openTools,
	setTools,
	hideTools,
}) => {
	const [columnDisplay, setColumnDisplay] = React.useState<
		ReadonlyArray<{ id: string; visible: boolean }>
	>(DEFAULT_COLUMN_DISPLAY);
	const [pageSize, setPageSize] = React.useState(20);
	const [stripedRows, setStripedRows] = React.useState(getDefaultStripedRows());
	const [wrapLines, setWrapLines] = React.useState(false);
	const [activeTabId, setActiveTabId] = React.useState('all');

	const [jwtToken, setJwtToken] = React.useState('');

	React.useEffect(() => {
		getJwtToken()
			.then((token) => setJwtToken(token))
			.catch(() => setJwtToken(''));
	}, []);

	const authReady = !!jwtToken;

	const domains = DOMAIN_OPTIONS.map((opt) => opt.value).join(',');

	const {
		isLoading: isTasksLoading,
		data: tasksData,
		error: tasksError,
	} = useFetchForSDS<{ supportedTasks: TaskTypeListItem[] }>({
		path: authReady ? `/tasks/${domains}` : '',
		method: 'GET',
		url: SNOW_NXT_WORKFLOW_ENDPOINT,
		headers: jwtToken
			? { 'x-jwt-token': jwtToken, 'x-user': 'snow-team' }
			: undefined,
	});

	const {
		isLoading: isOperationsLoading,
		data: operationsData,
		error: operationsError,
	} = useFetchForSDS<ListOperationsByStatusResponse>({
		path: authReady ? '/operation/listOperations/' : '',
		method: 'GET',
		url: SDS_APIGATEWAY_ENDPOINT,
		queryParams: { status: 'ACTIVE', count: 200 },
		headers: jwtToken
			? { 'x-jwt-token': jwtToken, 'x-user': 'snow-team' }
			: undefined,
	});

	const isLoading = !authReady || isTasksLoading || isOperationsLoading;
	const error = tasksError || operationsError;

	const taskTypes = React.useMemo(() => {
		const tasks = tasksData?.supportedTasks || [];
		const operations = convertOperationsIntoTaskSchema(
			operationsData?.operations || []
		);

		return [...tasks, ...operations];
	}, [tasksData, operationsData]);

	React.useEffect(() => {
		if (setTools) {
			setTools(HELP_PANEL_CONFIG);
		}

		return () => {
			if (hideTools) {
				hideTools();
			}
		};
	}, [setTools, hideTools]);

	const handleInfoClick = () => {
		if (openTools) {
			openTools();
		}
	};

	const filteredByType = React.useMemo(() => {
		if (activeTabId === 'all') return taskTypes;
		if (activeTabId === 'tasks') return taskTypes.filter((t) => !t.operation);
		if (activeTabId === 'operations')
			return taskTypes.filter((t) => t.operation);

		return taskTypes;
	}, [taskTypes, activeTabId]);

	const { items, collectionProps, filterProps, paginationProps } =
		useCollection(filteredByType, {
			filtering: {
				empty: <TableWithNoData message={error || 'No task types found'} />,
				noMatch: <TableWithNoData message="No matching task types" />,
			},
			pagination: { pageSize },
			sorting: { defaultState: { sortingColumn: COLUMN_DEFINITIONS[0] } },
		});

	const taskCount = taskTypes.filter((t) => !t.operation).length;
	const operationCount = taskTypes.filter((t) => t.operation).length;

	return (
		<SpaceBetween size="l">
			<Header
				variant="h1"
				description="Browse all supported task types and operations available for workflow creation"
				info={
					openTools && (
						<Link variant="info" onFollow={handleInfoClick}>
							Info
						</Link>
					)
				}
			>
				Supported Task Types & Operations
			</Header>

			{error && (
				<Flashbar
					items={[
						{
							type: 'error',
							header: 'Failed to load data',
							content:
								typeof error === 'string'
									? error
									: 'Unable to load task types and operations. Please try again.',
							id: 'fetch-error',
						},
					]}
				/>
			)}

			<Container>
				<ColumnLayout columns={3} variant="text-grid">
					<Box>
						<Box variant="awsui-key-label">Total Items</Box>
						<Box variant="awsui-value-large">
							{isLoading ? '-' : taskTypes.length}
						</Box>
					</Box>
					<Box>
						<Box variant="awsui-key-label">Task Types</Box>
						<Box variant="awsui-value-large">{isLoading ? '-' : taskCount}</Box>
					</Box>
					<Box>
						<Box variant="awsui-key-label">Operations</Box>
						<Box variant="awsui-value-large">
							{isLoading ? '-' : operationCount}
						</Box>
					</Box>
				</ColumnLayout>
			</Container>

			<Tabs
				activeTabId={activeTabId}
				onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
				tabs={[
					{ id: 'all', label: `All (${taskTypes.length})` },
					{ id: 'tasks', label: `Tasks (${taskCount})` },
					{ id: 'operations', label: `Operations (${operationCount})` },
				]}
			/>

			<Container>
				<Table
					{...collectionProps}
					loading={isLoading}
					loadingText="Loading task types..."
					variant="embedded"
					header={
						<Header variant="h2" counter={`(${filteredByType.length})`}>
							{activeTabId === 'all'
								? 'All Task Types & Operations'
								: activeTabId === 'tasks'
								? 'Task Types'
								: 'Operations'}
						</Header>
					}
					columnDefinitions={COLUMN_DEFINITIONS}
					columnDisplay={columnDisplay}
					items={items}
					filter={
						<TextFilter
							{...filterProps}
							filteringPlaceholder="Find task types or operations"
						/>
					}
					pagination={<Pagination {...paginationProps} />}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{
								pageSize,
								contentDisplay: columnDisplay.map((col) => ({
									id: col.id,
									visible: col.visible,
								})),
								stripedRows,
								wrapLines,
							}}
							onConfirm={({ detail }) => {
								if (detail.pageSize) {
									setPageSize(detail.pageSize);
								}

								if (detail.contentDisplay) {
									setColumnDisplay(detail.contentDisplay);
								}

								if (detail.stripedRows !== undefined) {
									setStripedRows(detail.stripedRows);
								}

								if (detail.wrapLines !== undefined) {
									setWrapLines(detail.wrapLines);
								}
							}}
							pageSizePreference={PAGE_SIZE_PREFERENCE}
							stripedRowsPreference={STRIPED_ROWS_PREFERENCE}
							wrapLinesPreference={WRAP_LINES_PREFERENCE}
							contentDisplayPreference={{
								title: 'Column preferences',
								options: COLUMN_DEFINITIONS.map((col) => ({
									id: col.id,
									label: col.header as string,
									alwaysVisible: col.id === 'taskName',
								})),
							}}
						/>
					}
					trackBy="taskId"
					stickyHeader
					stripedRows={stripedRows}
					wrapLines={wrapLines}
					empty={
						<TableWithNoData
							message={error || 'No task types or operations available.'}
						/>
					}
				/>
			</Container>
		</SpaceBetween>
	);
};

export default SupportedTaskTypes;
